import { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { TagType } from '@sib/api';
import { TaskField } from '@sib/utils/tasks';

interface TagFormValue {
  [TagType.Custom]: string[];
  [TagType.Contact]: string[];
  [TagType.Employee]: string[];
}

export const useTagSelection = () => {
  const { setValue } = useFormContext();
  const value = useWatch({ name: TaskField.Tag }) as TagFormValue;

  const toggleTag = useCallback(
    (optionValue: string, field?: string) => {
      const fieldToUpdate = (field || TagType.Custom) as keyof TagFormValue;
      const fieldPath = `${TaskField.Tag}.${fieldToUpdate}`;
      const currentFieldValue = value[fieldToUpdate] || [];
      const isSelected = currentFieldValue.includes(optionValue);

      if (isSelected) {
        setValue(
          fieldPath,
          currentFieldValue.filter((v: string) => v !== optionValue)
        );

        return false;
      }

      setValue(fieldPath, [...currentFieldValue, optionValue]);

      return true;
    },
    [setValue, value]
  );

  const removeTag = useCallback(
    (optionValue: string | string[], field?: string) => {
      const fieldToUpdate = (field || TagType.Custom) as keyof TagFormValue;
      const fieldPath = `${TaskField.Tag}.${fieldToUpdate}`;
      const currentFieldValue = value[fieldToUpdate] || [];
      const isArray = Array.isArray(optionValue);
      const valuesToRemove = isArray
        ? new Set(optionValue)
        : new Set([optionValue]);

      setValue(
        fieldPath,
        currentFieldValue.filter((v: string) => !valuesToRemove.has(v))
      );
    },
    [setValue, value]
  );

  const clearAllTags = useCallback(
    () =>
      setValue(TaskField.Tag, {
        [TagType.Custom]: [],
        [TagType.Contact]: [],
        [TagType.Employee]: [],
      }),
    [setValue]
  );

  const allSelectedValues = Object.values(value).flat() as string[];

  return {
    toggleTag,
    removeTag,
    clearAllTags,
    allSelectedValues,
    selectedTagsCount: allSelectedValues.length,
  };
};
